import React, { Fragment, Component, PureComponent } from "react";
import styled, { keyframes, css } from "styled-components";
import Observer from "@researchgate/react-intersection-observer";
import FaBriefcase from "react-icons/lib/fa/briefcase";
import { basename } from "path";
import { RandomDelayedLetter } from "../../containers/Transitions";
import {
  LoadAdaptedImage,
  ProgressiveImage,
  findOptimalImage
} from "../../containers/ProgressiveHelpers";
import {
  Stretched,
  AbsoluteStretched,
  Centered,
  Logo,
  LogoStyle
} from "../../containers/Layout";
import {
  StickedNavbar,
  FluentNavbar,
  links
} from "../../segments/NavbarSegment";
import PreserveDimensions from "../../containers/PreserveDimensions";

const HeroContainer = styled(PreserveDimensions)`
  overflow: hidden;
  position: relative;
  height: 100vh;
  width: 100%;
`;

const fadeout = keyframes`
  from {
    opacity: 1;
  }

  to {
    opacity: 0;
  }
`;

const fadein = keyframes`
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
`;

const Background = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background-image: url(${props => props.src});
  background-attachment: fixed;
  background-size: cover;
  background-position: center;
  animation: ${props =>
    props.transitioning &&
    css`
      ${fadeout} 1s forwards;
    `};

  @media (pointer: coarse) and (hover: none) {
    background-attachment: scroll;
  }
`;

const Placeholder = styled(Background)`
  filter: blur(15px);
  transform: scale(1.1);
`;

const SlideshowStyle = styled.div`
  animation: ${fadein} 1.5s forwards;
`;

class Slideshow extends Component {
  state = {};

  constructor(props) {
    super(props);

    this.element = typeof document !== "undefined" && document.documentElement;
  }

  static getKey = (image, images) => image + "--" + images.indexOf(image);

  static getDerivedStateFromProps = (nextProps, prevState) => {
    if (nextProps.images === prevState.images) {
      return prevState;
    }

    const state = {
      active:
        prevState.active !== undefined ? prevState.active : nextProps.images[0],
      nextActive:
        nextProps.images.length >= 1
          ? nextProps.images[1]
          : nextProps.images[0],
      images: nextProps.images
    };

    return {
      ...state,
      key: Slideshow.getKey(state.active, state.images),
      nextKey: Slideshow.getKey(state.nextActive, state.images)
    };
  };

  componentDidMount = () => {
    this.interval = setInterval(this.triggerTransition, 5000);
  };

  componentWillUnmount = () => {
    clearInterval(this.interval);
  };

  triggerTransition = () => {
    this.setState({
      trigger: true
    });
  };

  next = () => {
    const nextActiveIndex = this.state.images.indexOf(this.state.nextActive);
    const newNextActive =
      nextActiveIndex + 1 < this.state.images.length
        ? this.state.images[nextActiveIndex + 1]
        : this.state.images[0];
    this.setState({
      active: this.state.nextActive,
      nextActive: newNextActive,
      key: this.state.nextKey,
      nextKey: Slideshow.getKey(newNextActive, this.state.images),
      trigger: false
    });
  };

  render = () => {
    return (
      <Fragment>
        <ProgressiveImage
          {...this.state.nextActive}
          key={this.state.nextKey}
          src={findOptimalImage(this.element, this.state.nextActive)}
          children={({ src, isPlaceholder }) =>
            isPlaceholder ? <Placeholder src={src} /> : <Background src={src} />
          }
        />
        <ProgressiveImage
          {...this.state.active}
          key={this.state.key}
          src={findOptimalImage(this.element, this.state.active)}
          children={({ src, isPlaceholder }) =>
            isPlaceholder ? (
              <Placeholder
                src={src}
                onAnimationEnd={this.next}
                transitioning={this.state.trigger}
              />
            ) : (
              <Background
                src={src}
                onAnimationEnd={this.next}
                transitioning={this.state.trigger}
              />
            )
          }
        />
      </Fragment>
    );
  };
}

const JobsButton = styled.a`
  all: initial;
  right: 0;
  position: absolute;
  top: 0;
  z-index: 100;
  margin: 2.5em;
  border-radius: 2.5em;
  border-color: rgba(153, 128, 214, 0.75);
  border-width: 0.275em;
  display: inline-flex;
  background-color: aliceblue;
  height: 2.5em;
  font-family: "Raleway", sans-serif;
  font-weight: 500;
  background: linear-gradient(30deg, rgb(87, 124, 255), rgb(111, 54, 255));
  color: white;
  justify-content: space-around;
  align-items: center;
  padding: 0.5em 2em 0.5em 2em;
  border-style: solid;
  transition: opacity 1s;
  cursor: pointer;
  transition: transform 0.5s;
  font-size: inherit;
  &:after {
    background: linear-gradient(30deg, rgb(111, 54, 255), rgb(87, 124, 255));
    border-radius: 2.5em;
    opacity: 0;
    width: 100%;
    height: 100%;
    content: "";
    position: absolute;
    z-index: -1;
    transition: opacity 0.5s;
  }

  &:hover:after {
    opacity: 1;
  }

  &:hover {
    transform: scale(0.95);
  }

  @media (max-width: 540px) {
    font-size: 1.25em;
  }

  @media (max-width: 1810px) {
    padding-left: 3em;
    padding-right: 3em;
  }
`;

export const SlightShade = styled(AbsoluteStretched)`
  background: radial-gradient(hsla(0, 0%, 83%, 0.52), rgba(0, 0, 0, 0));
`;

const HeroTitle = styled.h1`
  font-family: "Raleway", sans-serif;
  font-weight: 500;
  font-size: 3.5em;
  color: rgb(115, 126, 255);
  position: relative;
`;

class HeroSection extends Component {
  constructor() {
    super();
    this.state = {
      insideViewport: true
    };
  }

  handleChange = entry =>
    this.setState({
      insideViewport: entry.isIntersecting
    });

  render() {
    const { cms } = this.props;

    return (
      <Observer onChange={this.handleChange}>
        <HeroContainer>
          <JobsButton href="/jobs">
            <FaBriefcase style={{ paddingRight: "0.5em", fontSize: "1.5em" }} />
            Jobs
          </JobsButton>
          <Slideshow
            images={cms["hero-section-background-images"].map(filename =>
              require(`../../../public/uploads/${basename(filename)}`)
            )}
          />
          <Centered>
            <SlightShade />
            <HeroTitle>
              <Centered>
                <Logo />
              </Centered>
              {this.state.insideViewport && (
                <Fragment>
                  {Array.from(cms["hero-section-title"]).map(
                    (char, index) =>
                      /\S/.test(char) ? (
                        <RandomDelayedLetter key={index} children={char} />
                      ) : (
                        " "
                      )
                  )}
                </Fragment>
              )}
            </HeroTitle>
          </Centered>
          {this.state.insideViewport ? (
            <FluentNavbar links={links} />
          ) : (
            <StickedNavbar links={links} />
          )}
        </HeroContainer>
      </Observer>
    );
  }
}

export default HeroSection;



// WEBPACK FOOTER //
// /opt/build/repo/src/pages/Landing/HeroSection.js